import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getCastellerSearchText, getCastellerShortName } from '../../../../utils/castellerName';

// Create a memoized casteller item component
const CastellerItem = React.memo(({ assist, isSelected, onClick, isInTronc }) => (
    <div
        className={`cl-item ${isSelected ? 'cl-item-selected' : ''} ${isInTronc ? 'cl-item-in-tronc' : ''}`}
        onClick={onClick}
        style={{
            color: assist['assistència'] === 2 ? 'darkblue' :
                   assist['assistència'] === 1 ? 'green' :
                   assist['assistència'] === 0 ? 'red' :
                   'darkorange'
        }}
    >
        <span className="cl-item-name">{getCastellerShortName(assist)}</span>
        {assist.altura ? <span className="cl-item-altura">{assist.altura}</span> : null}
    </div>
));

function CastellersList({ 
    assistencies, 
    selectedTronc, 
    currentCastellersInTronc, 
    hasCastellerInSelectedBox,
    canSwap,
    onCastellerSelect,
    onSwap,
    selectedCasteller,
    selectedBox,
    etiquetes = [],
    etiquetaUsers = {}
}) {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [showEtiqueta, setShowEtiqueta] = useState({
        'No confirmats': true,
        'No venen': true
    });
    const [filterMode, setFilterMode] = useState('etiquetes'); // 'etiquetes', 'no-confirmats', 'no-venen'
    const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth <= 768;
    const [isMobile, setIsMobile] = useState(getIsMobile);
    const [isListVisible, setIsListVisible] = useState(() => !getIsMobile());

    // Check if the device is mobile
    useEffect(() => {
        const checkIfMobile = () => {
            const mobile = getIsMobile();
            setIsMobile(mobile);

            // Keep the list hidden on mobile unless a box is selected
            if (!mobile) {
                setIsListVisible(true);
            } else if (!selectedBox) {
                setIsListVisible(false);
            }
        };

        // Initial check
        checkIfMobile();

        // Add event listener for window resize
        window.addEventListener('resize', checkIfMobile);

        // Cleanup
        return () => window.removeEventListener('resize', checkIfMobile);
    }, [selectedBox]);

    // Show the list on mobile only after a box has been selected
    useEffect(() => {
        if (!isMobile) return;
        setIsListVisible(!!selectedBox);
    }, [selectedBox, isMobile]);

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            
            // When searching, make sure all groups are open
            if (searchQuery) {
                setShowEtiqueta(prev => ({
                    ...prev,
                    'Venen': true,
                    'No confirmats': true,
                    'No venen': true
                }));
            }
        }, 150);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Add text normalization function
    const normalizeText = (text) => {
        return text
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Remove accents
            .replace(/[^a-z0-9]/g, ''); // Remove special characters and spaces
    };

    // Memoize filtered assistencies with normalized search
    const filteredAssistencies = useMemo(() => {
        return assistencies
            .filter(assist => {
                // When searching, don't filter out anyone
                if (debouncedSearchQuery) {
                    return true;
                }
                // When not searching, filter out hidden, lesionat, and non-castellers
                return parseInt(assist.casteller) === 1 && !parseInt(assist.lesionat) && !parseInt(assist.hidden);
            })
            .filter(assist => {
                // Apply filter based on filterMode, but only if not searching
                if (!debouncedSearchQuery) {
                    if (filterMode === 'no-confirmats') {
                        // No confirmats are those who are not confirmed (not 0, 1, or 2)
                        return assist['assistència'] !== 0 && assist['assistència'] !== 1 && assist['assistència'] !== 2;
                    } else if (filterMode === 'no-venen') {
                        // No venen (No vinc) has assistència value of 0
                        return assist['assistència'] === 0;
                    } else {
                        return assist['assistència'] === 1 || assist['assistència'] === 2;
                    }
                }
                // When searching, don't filter by assistència
                return true;
            })
            .filter(assist => {
                if (!debouncedSearchQuery) return true;
                const searchNormalized = normalizeText(debouncedSearchQuery);
                const fullName = normalizeText(getCastellerSearchText(assist));
                return fullName.includes(searchNormalized);
            });
    }, [assistencies, debouncedSearchQuery, filterMode]);

    // Memoize handlers
    const handleCastellerClick = useCallback((castellerId) => {
        onCastellerSelect(castellerId);
        setSearchQuery(''); // Clear search input after selection
        
        // On mobile, hide the list after selection
        if (isMobile) {
            setIsListVisible(false);
        }
    }, [onCastellerSelect, isMobile]);

    // Sort castellers by altura (ascending - smaller heights first)
    const sortByAltura = (castellers) => {
        return [...castellers].sort((a, b) => {
            const alturaA = a.altura || 999;
            const alturaB = b.altura || 999;
            return alturaA - alturaB;
        });
    };

    // Group castellers by etiquetes
    const groupedCastellers = useMemo(() => {
        if (debouncedSearchQuery) {
            // When searching, show castellers grouped by their attendance status
            const venen = filteredAssistencies.filter(assist => 
                assist['assistència'] === 1 || assist['assistència'] === 2
            );
            
            const noConfirmats = filteredAssistencies.filter(assist => 
                assist['assistència'] !== 0 && assist['assistència'] !== 1 && assist['assistència'] !== 2
            );
            
            const noVenen = filteredAssistencies.filter(assist => 
                assist['assistència'] === 0
            );
            
            const result = [];
            
            if (venen.length > 0) {
                result.push({
                    perfil: 'Venen',
                    castellers: sortByAltura(venen),
                    noGroups: true
                });
            }
            
            if (noConfirmats.length > 0) {
                result.push({
                    perfil: 'No confirmats',
                    castellers: sortByAltura(noConfirmats),
                    noGroups: true
                });
            }
            
            if (noVenen.length > 0) {
                result.push({
                    perfil: 'No venen',
                    castellers: sortByAltura(noVenen),
                    noGroups: true
                });
            }
            
            return result;
        }

        if (filterMode === 'no-confirmats') {
            return [{
                perfil: 'No confirmats',
                castellers: sortByAltura(filteredAssistencies),
                noGroups: true
            }];
        }

        if (filterMode === 'no-venen') {
            return [{
                perfil: 'No venen',
                castellers: sortByAltura(filteredAssistencies),
                noGroups: true
            }];
        }

        const groups = etiquetes
            .map(({ id }) => etiquetaUsers?.[id])
            .filter(etiquetaUsers => etiquetaUsers?.castellers?.length > 0)
            .sort((a, b) => {
                if (a.perfil === 'Tots') return -1;
                if (b.perfil === 'Tots') return 1;
                return a.perfil.localeCompare(b.perfil);
            })
            .map(group => ({
                perfil: group.perfil,
                castellers: sortByAltura(filteredAssistencies.filter(assist => 
                    group.castellers.some(c => c.id === assist.id)
                ))
            }))
            .filter(group => group.castellers.length > 0);

        // Add "No etiquetat" group for castellers without any label
        const untaggedCastellers = filteredAssistencies.filter(assist => 
            !etiquetes.some(e => 
                etiquetaUsers?.[e.id]?.castellers?.some(c => c.id === assist.id)
            )
        );

        if (untaggedCastellers.length > 0) {
            groups.push({
                perfil: 'No etiquetat',
                castellers: sortByAltura(untaggedCastellers)
            });
        }

        return groups;
    }, [filteredAssistencies, etiquetes, etiquetaUsers, debouncedSearchQuery, filterMode]);

    return (
        <>
            <div className={`cl-panel ${isMobile ? 'cl-mobile' : ''} ${isListVisible || !isMobile ? 'cl-visible' : 'cl-hidden'}`}>
                <div className="cl-header">
                    <div className="cl-header-row">
                        <div className="cl-search-container">
                            <input
                                type="text"
                                placeholder="Cerca casteller..."
                                className="cl-search-input"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    className="cl-clear-search"
                                    onClick={() => setSearchQuery('')}
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                        <div className="cl-action-buttons">
                            <button 
                                className="cl-action-button cl-remove-button"
                                onClick={() => onCastellerSelect(null)}
                                style={{
                                    opacity: hasCastellerInSelectedBox ? 1 : 0.3,
                                    pointerEvents: hasCastellerInSelectedBox ? 'auto' : 'none'
                                }}
                            >
                                🗑️
                            </button>
                            <button 
                                className="cl-action-button cl-swap-button"
                                onClick={onSwap}
                                style={{
                                    opacity: canSwap ? 1 : 0.3,
                                    pointerEvents: canSwap ? 'auto' : 'none'
                                }}
                            >
                                🔄
                            </button>
                        </div>
                    </div>
                    
                    {!debouncedSearchQuery && (
                        <div className="cl-filter-tabs">
                            <button 
                                className={`cl-filter-tab ${filterMode === 'etiquetes' ? 'cl-active' : ''}`}
                                onClick={() => setFilterMode('etiquetes')}
                            >
                                Venen
                            </button>
                            <button 
                                className={`cl-filter-tab ${filterMode === 'no-confirmats' ? 'cl-active' : ''}`}
                                onClick={() => setFilterMode('no-confirmats')}
                            >
                                No confirmats
                            </button>
                            <button 
                                className={`cl-filter-tab ${filterMode === 'no-venen' ? 'cl-active' : ''}`}
                                onClick={() => setFilterMode('no-venen')}
                            >
                                No venen
                            </button>
                        </div>
                    )}
                </div>

                <div className="cl-groups">
                    {debouncedSearchQuery && groupedCastellers.length === 0 ? (
                        <div className="cl-no-results">
                            Cap resultat per "{debouncedSearchQuery}"
                        </div>
                    ) : null}
                    {groupedCastellers.map(({ perfil, castellers, noGroups }) => {
                        const availableCastellers = castellers.filter(c => !currentCastellersInTronc.includes(c.id));
                        const hasAvailableCastellers = availableCastellers.length > 0;

                        // For No confirmats and No venen, show a simple list without groups
                        if (noGroups) {
                            return (
                                <div key={perfil} className="cl-group">
                                    <div 
                                        className="cl-group-header"
                                        onClick={() => setShowEtiqueta(prev => ({
                                            ...prev,
                                            [perfil]: !prev[perfil]
                                        }))}
                                    >
                                        <div className="cl-group-arrow">
                                            {showEtiqueta[perfil] ? '↓' : '→'}
                                        </div>
                                        <div className="cl-group-title">
                                            {perfil.toUpperCase()}
                                        </div>
                                        {hasAvailableCastellers && (
                                            <div className="cl-group-badge">
                                                {availableCastellers.length}
                                            </div>
                                        )}
                                    </div>

                                    {showEtiqueta[perfil] && (
                                        <div className="cl-grid">
                                            {castellers.map(assist => (
                                                <CastellerItem
                                                    key={assist.id}
                                                    assist={assist}
                                                    isSelected={selectedCasteller === assist.id}
                                                    onClick={() => handleCastellerClick(assist.id)}
                                                    isInTronc={selectedTronc && currentCastellersInTronc.includes(assist.id)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        // For Venen (etiquetes), show the grouped list
                        return (
                            <div key={perfil} className="cl-group">
                                <div 
                                    className="cl-group-header"
                                    onClick={() => setShowEtiqueta(prev => ({
                                        ...prev,
                                        [perfil]: !prev[perfil]
                                    }))}
                                >
                                    <div className="cl-group-arrow">
                                        {showEtiqueta[perfil] ? '↓' : '→'}
                                    </div>
                                    <div className="cl-group-title">
                                        {perfil.toUpperCase()}
                                    </div>
                                    {hasAvailableCastellers && (
                                        <div className="cl-group-badge">
                                            {availableCastellers.length}
                                        </div>
                                    )}
                                </div>

                                {showEtiqueta[perfil] && (
                                    <div className="cl-grid">
                                        {castellers.map(assist => (
                                            <CastellerItem
                                                key={assist.id}
                                                assist={assist}
                                                isSelected={selectedCasteller === assist.id}
                                                onClick={() => handleCastellerClick(assist.id)}
                                                isInTronc={selectedTronc && currentCastellersInTronc.includes(assist.id)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <style jsx>{`
                    .cl-panel {
                        width: 25%;
                        min-width: 150px;
                        max-width: 220px;
                        background: white;
                        border-radius: 8px;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        padding: 0.75rem;
                        display: flex;
                        flex-direction: column;
                        gap: 0.75rem;
                        height: 60vh;
                        overflow: hidden;
                        flex-shrink: 0;
                        transition: transform 0.3s ease-in-out;
                    }

                    /* Mobile styles */
                    .cl-panel.cl-mobile {
                        position: fixed;
                        bottom: 0;
                        left: 0;
                        right: 0;
                        width: 95%;
                        height: 35vh;
                        z-index: 1000;
                        border-radius: 8px 8px 0 0;
                        box-shadow: 0 -2px 10px rgba(0,0,0,0.2);
                        max-width: 500px;
                        margin: 0 auto;
                    }

                    .cl-panel.cl-mobile.cl-hidden {
                        transform: translateY(100%);
                    }

                    .cl-panel.cl-mobile.cl-visible {
                        transform: translateY(0);
                    }

                    .cl-header {
                        padding-bottom: 0.75rem;
                        border-bottom: 1px solid #eee;
                        display: flex;
                        flex-direction: column;
                        gap: 0.75rem;
                        flex-shrink: 0;
                    }

                    .cl-header-row {
                        display: flex;
                        flex-direction: row;
                        flex-wrap: nowrap;
                        gap: 0.5rem;
                        align-items: center;
                        width: 100%;
                    }

                    .cl-search-container {
                        position: relative;
                        flex: 1 1 auto;
                        min-width: 0;
                    }

                    .cl-search-input {
                        width: 100%;
                        padding: 0.4rem;
                        padding-right: 1.5rem;
                        border: 1px solid #ddd;
                        border-radius: 4px;
                        font-size: 0.8rem;
                        height: 28px;
                    }

                    .cl-clear-search {
                        position: absolute;
                        right: 0.3rem;
                        top: 50%;
                        transform: translateY(-50%);
                        background: transparent;
                        border: none;
                        width: 16px;
                        height: 16px;
                        border-radius: 50%;
                        cursor: pointer;
                        font-size: 0.7rem;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: #666;
                        padding: 0;
                    }

                    .cl-filter-tabs {
                        display: flex;
                        gap: 0.25rem;
                        width: 100%;
                    }

                    .cl-filter-tab {
                        flex: 1;
                        padding: 0.4rem 0.25rem;
                        border: 1px solid #ddd;
                        border-radius: 4px;
                        background: #f5f5f5;
                        font-size: 0.7rem;
                        cursor: pointer;
                        text-align: center;
                        transition: all 0.2s;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        color: black;
                    }

                    .cl-filter-tab:hover {
                        background: #eee;
                    }

                    .cl-filter-tab.cl-active {
                        background: #2859A8;
                        color: white;
                        border-color: #2859A8;
                    }

                    .cl-action-buttons {
                        display: flex;
                        flex: 0 0 auto;
                        gap: 0.25rem;
                    }

                    .cl-action-button {
                        width: 28px;
                        height: 28px;
                        padding: 0.4rem;
                        border: none;
                        border-radius: 4px;
                        cursor: pointer;
                        font-size: 1rem;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        transition: all 0.2s;
                    }

                    .cl-remove-button {
                        background: #ffeeee;
                    }

                    .cl-remove-button:hover {
                        background: #ffdddd;
                    }

                    .cl-swap-button {
                        background: #eeeeff;
                    }

                    .cl-swap-button:hover {
                        background: #ddddff;
                    }

                    .cl-groups {
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                        overflow-y: auto;
                        flex: 1;
                        min-height: 0;
                    }

                    .cl-no-results {
                        padding: 1rem;
                        text-align: center;
                        color: #666;
                        font-style: italic;
                    }

                    .cl-group {
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                    }

                    .cl-group-header {
                        display: flex;
                        align-items: center;
                        padding: 0.5rem;
                        background: #f5f5f5;
                        border-radius: 4px;
                        cursor: pointer;
                        transition: background-color 0.2s;
                        height: 32px;
                        min-height: 32px;
                    }

                    .cl-group-header:hover {
                        background: #eee;
                    }

                    .cl-group-arrow {
                        width: 24px;
                        color: #666;
                        display: flex;
                        align-items: center;
                    }

                    .cl-group-title {
                        flex: 1;
                        font-weight: 500;
                        font-size: 0.8rem;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .cl-group-badge {
                        background: #2859A8;
                        color: white;
                        padding: 0.2rem 0.5rem;
                        border-radius: 4px;
                        font-size: 0.8rem;
                        min-width: 24px;
                        height: 20px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .cl-grid {
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                    }

                    .cl-item {
                        padding: 0.5rem;
                        border: 1px solid #ddd;
                        border-radius: 4px;
                        cursor: pointer;
                        transition: all 0.2s;
                        height: 32px;
                        min-height: 32px;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                    }

                    .cl-item-name {
                        flex: 1;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    .cl-item-altura {
                        font-size: 0.75rem;
                        color: #666;
                        background: #f0f0f0;
                        padding: 0.1rem 0.4rem;
                        border-radius: 3px;
                        margin-left: 0.5rem;
                        flex-shrink: 0;
                    }

                    .cl-item:hover {
                        background: #f5f5f5;
                    }

                    .cl-item.cl-item-selected {
                        background: #f0f7ff;
                        border-color: #2859A8;
                    }

                    .cl-item.cl-item-in-tronc {
                        background: rgba(100, 100, 100, 0.15);
                        border-color: #bbb;
                    }

                    .cl-item.cl-item-in-tronc .cl-item-name {
                        text-decoration: line-through;
                        opacity: 0.6;
                    }

                    .cl-item.cl-item-in-tronc .cl-item-altura {
                        opacity: 0.6;
                    }

                    /* Additional mobile styles for better touch interaction */
                    @media (max-width: 768px) {
                        .cl-item {
                            height: 40px;
                            min-height: 40px;
                            font-size: 1rem;
                        }

                        .cl-group-header {
                            height: 40px;
                            min-height: 40px;
                        }

                        .cl-group-title {
                            font-size: 0.9rem;
                        }

                        .cl-filter-tab {
                            font-size: 0.8rem;
                            padding: 0.5rem 0.25rem;
                        }

                        .cl-search-input {
                            height: 36px;
                            font-size: 1rem;
                        }

                        .cl-action-button {
                            width: 36px;
                            height: 36px;
                        }
                    }
                `}</style>
            </div>
        </>
    );
}

export default React.memo(CastellersList);
